import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { 
  Users, 
  TrendUp, 
  UserMinus,
  CalendarBlank,
  Info,
  Trophy,
  Warning,
  ChartBar,
  UsersThree,
  ShieldCheck,
  ClipboardText,
  ArrowUpRight,
  ArrowDownLeft,
  Minus,
  FilePpt,
  FileXls,
  FilePdf,
  DownloadSimple,
  CaretDown
} from '@phosphor-icons/react'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import pptxgen from 'pptxgenjs'
import ExcelJS from 'exceljs'
import {
  VictoryChart,
  VictoryLine,
  VictoryBar,
  VictoryPie,
  VictoryAxis,
  VictoryTheme,
  VictoryTooltip,
  VictoryGroup,
  VictoryLegend
} from 'victory'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  ResponsiveContainer,
  Cell
} from 'recharts'

export default function ReportsPage() {
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [organization, setOrganization] = useState(null)
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  
  // Overview stats
  const [totalMembers, setTotalMembers] = useState(0)
  const [totalEvents, setTotalEvents] = useState(0)
  const [avgAttendanceRate, setAvgAttendanceRate] = useState(0)
  const [unassignedMembers, setUnassignedMembers] = useState(0)
  const [totalTeams, setTotalTeams] = useState(0)
  const [totalGroups, setTotalGroups] = useState(0)
  const [totalSubgroups, setTotalSubgroups] = useState(0)
  
  // Period comparison filters
  const [selectedMonth1, setSelectedMonth1] = useState('')
  const [selectedMonth2, setSelectedMonth2] = useState('')
  const [monthOptions, setMonthOptions] = useState([])
  
  // Comparison data
  const [month1Data, setMonth1Data] = useState({
    totalMembers: 0,
    avgAttendanceRate: 0,
    totalEvents: 0,
    unassignedMembers: 0,
    totalTeams: 0,
    totalGroups: 0,
    totalSubgroups: 0
  })
  const [month2Data, setMonth2Data] = useState({
    totalMembers: 0,
    avgAttendanceRate: 0,
    totalEvents: 0,
    unassignedMembers: 0,
    totalTeams: 0,
    totalGroups: 0,
    totalSubgroups: 0
  })

  // Events data
  const [allEvents, setAllEvents] = useState([])
  const [eventTypeFilter, setEventTypeFilter] = useState('all')
  const [eventSortOrder, setEventSortOrder] = useState('highest') // highest, lowest

  // Attendance data
  const [avgAttendancePerEvent, setAvgAttendancePerEvent] = useState(0)
  const [eventFillRate, setEventFillRate] = useState(0)
  const [confirmationRatio, setConfirmationRatio] = useState(0)
  const [noShowRate, setNoShowRate] = useState(0)
  const [attendanceTrendData, setAttendanceTrendData] = useState([])
  const [selectedTrendMonth, setSelectedTrendMonth] = useState('')

  // Hierarchy data
  const [hierarchyLevel, setHierarchyLevel] = useState('team')
  const [hierarchyRankings, setHierarchyRankings] = useState([])

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBar },
    { id: 'events', label: 'Events', icon: CalendarBlank },
    { id: 'attendance', label: 'Attendance', icon: Users },
    { id: 'hierarchy', label: 'Structure', icon: UsersThree }
  ]

  useEffect(() => {
    loadData()
    
    // Generate last 12 months
    const options = []
    const now = new Date()
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthName = format(date, 'MMMM yyyy')
      const value = format(date, 'yyyy-MM')
      options.push({ label: monthName, value })
    }
    setMonthOptions(options)
    
    // Set defaults: current month vs previous month
    if (options.length >= 2) {
      setSelectedMonth1(options[0].value) // Current month
      setSelectedMonth2(options[1].value) // Previous month
      setSelectedTrendMonth(options[0].value) // Current month for trend
    }
  }, [])

  useEffect(() => {
    if (organization && selectedMonth1 && selectedMonth2 && activeTab === 'overview') {
      loadComparisonData()
    } else if (organization && selectedTrendMonth && activeTab === 'attendance') {
      loadAttendanceData()
    }
  }, [selectedMonth1, selectedMonth2, selectedTrendMonth, organization, activeTab])

  useEffect(() => {
    if (organization) {
      if (activeTab === 'overview') {
        // Comparison data loaded by other useEffect
      } else if (activeTab === 'events') {
        loadEventsData()
      } else if (activeTab === 'attendance') {
        loadAttendanceData()
      } else if (activeTab === 'hierarchy') {
        loadHierarchyData()
      }
    }
  }, [activeTab, organization, hierarchyLevel])

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportDropdown && !event.target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showExportDropdown])

  const loadData = async () => {
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      if (!userData?.org_id) return

      const { data: orgData } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', userData.org_id)
        .single()

      setOrganization(orgData)
      
      // Load events and hierarchy data immediately for reports
      // (Attendance data loads separately when selectedTrendMonth is set)
      await Promise.all([
        loadEventsData(),
        loadHierarchyData()
      ])
    } catch (error) {
      console.error('Load data error:', error)
      toast.error('Failed to load reports')
    } finally {
      setLoading(false)
    }
  }

  const loadComparisonData = async () => {
    if (!organization || !selectedMonth1 || !selectedMonth2) return

    try {
      // Parse selected months
      const [year1, month1] = selectedMonth1.split('-').map(Number)
      const [year2, month2] = selectedMonth2.split('-').map(Number)
      
      const startOfMonth1 = new Date(year1, month1 - 1, 1)
      const endOfMonth1 = new Date(year1, month1, 0, 23, 59, 59)
      const startOfMonth2 = new Date(year2, month2 - 1, 1)
      const endOfMonth2 = new Date(year2, month2, 0, 23, 59, 59)

      // Load data for month 1
      const data1 = await loadMonthData(startOfMonth1, endOfMonth1)
      setMonth1Data(data1)

      // Load data for month 2
      const data2 = await loadMonthData(startOfMonth2, endOfMonth2)
      setMonth2Data(data2)

    } catch (error) {
      console.error('Load comparison data error:', error)
    }
  }

  const loadMonthData = async (startDate, endDate) => {
    try {
      // Total members at end of month
      const { count: membersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .lte('created_at', endDate.toISOString())

      // Unassigned members
      const { count: unassignedCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .eq('role', 'unassigned')
        .lte('created_at', endDate.toISOString())

      // Hierarchy counts
      const { count: teamsCount } = await supabase
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .eq('depth', 1)
        .lte('created_at', endDate.toISOString())

      const { count: groupsCount } = await supabase
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .eq('depth', 2)
        .lte('created_at', endDate.toISOString())

      const { count: subgroupsCount } = await supabase
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .eq('depth', 3)
        .lte('created_at', endDate.toISOString())

      // Events in the month
      const { count: eventsCount } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .gte('start_date', startDate.toISOString())
        .lte('start_date', endDate.toISOString())

      // Average attendance rate for events in this month
      const { data: monthEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', organization.id)
        .gte('start_date', startDate.toISOString())
        .lte('start_date', endDate.toISOString())

      let avgRate = 0
      if (monthEvents && monthEvents.length > 0 && membersCount > 0) {
        let totalRate = 0
        for (const event of monthEvents) {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)
          
          const eventRate = ((attendedCount || 0) / membersCount) * 100
          totalRate += eventRate
        }
        avgRate = Math.round(totalRate / monthEvents.length)
      }

      return {
        totalMembers: membersCount || 0,
        avgAttendanceRate: avgRate,
        totalEvents: eventsCount || 0,
        unassignedMembers: unassignedCount || 0,
        totalTeams: teamsCount || 0,
        totalGroups: groupsCount || 0,
        totalSubgroups: subgroupsCount || 0
      }
    } catch (error) {
      console.error('Load month data error:', error)
      return {
        totalMembers: 0,
        avgAttendanceRate: 0,
        totalEvents: 0,
        unassignedMembers: 0,
        totalTeams: 0,
        totalGroups: 0,
        totalSubgroups: 0
      }
    }
  }

  const getComparison = (value1, value2, isPercentage = false) => {
    const diff = value1 - value2
    const percentChange = value2 > 0 ? Math.round((diff / value2) * 100) : 0
    
    let icon = null
    let color = ''
    let text = ''
    
    if (diff > 0) {
      icon = 'up'
      color = 'text-green-600'
      text = isPercentage 
        ? `${diff > 0 ? '+' : ''}${diff}% vs ${monthOptions.find(m => m.value === selectedMonth2)?.label || 'previous'}`
        : `+${percentChange}% vs ${monthOptions.find(m => m.value === selectedMonth2)?.label || 'previous'}`
    } else if (diff < 0) {
      icon = 'down'
      color = 'text-red-600'
      text = isPercentage
        ? `${diff}% vs ${monthOptions.find(m => m.value === selectedMonth2)?.label || 'previous'}`
        : `${percentChange}% vs ${monthOptions.find(m => m.value === selectedMonth2)?.label || 'previous'}`
    } else {
      icon = 'same'
      color = 'text-gray-500'
      text = 'No change'
    }
    
    return { icon, color, text, diff }
  }

  const loadOverviewData = async () => {
    if (!organization) return

    try {
      // Total members
      const { count: membersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)

      setTotalMembers(membersCount || 0)

      // Total events
      const { count: eventsCount } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)

      setTotalEvents(eventsCount || 0)

      // Unassigned members
      const { count: unassignedCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .eq('role', 'unassigned')

      setUnassignedMembers(unassignedCount || 0)

      // Hierarchy counts
      const { count: teamsCount } = await supabase
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .eq('depth', 1)

      setTotalTeams(teamsCount || 0)

      const { count: groupsCount } = await supabase
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .eq('depth', 2)

      setTotalGroups(groupsCount || 0)

      const { count: subgroupsCount } = await supabase
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)
        .eq('depth', 3)

      setTotalSubgroups(subgroupsCount || 0)

      // Average attendance rate (Formula 3)
      const { data: pastEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', organization.id)
        .lte('start_date', new Date().toISOString())

      if (pastEvents && pastEvents.length > 0) {
        let totalRate = 0
        for (const event of pastEvents) {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)

          const eventRate = membersCount > 0 ? ((attendedCount || 0) / membersCount) * 100 : 0
          totalRate += eventRate
        }
        const avgRate = pastEvents.length > 0 ? totalRate / pastEvents.length : 0
        setAvgAttendanceRate(Math.round(avgRate))
      } else {
        setAvgAttendanceRate(0)
      }

      // Period comparison
      const now = new Date()
      const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)

      // This month events
      const { data: thisMonthEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', organization.id)
        .gte('start_date', startOfThisMonth.toISOString())
        .lte('start_date', now.toISOString())

      let thisMonthAvg = 0
      if (thisMonthEvents && thisMonthEvents.length > 0) {
        let totalRate = 0
        for (const event of thisMonthEvents) {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)
          
          const eventRate = membersCount > 0 ? ((attendedCount || 0) / membersCount) * 100 : 0
          totalRate += eventRate
        }
        thisMonthAvg = Math.round(totalRate / thisMonthEvents.length)
      }
      setThisMonthRate(thisMonthAvg)

      // Last month events
      const { data: lastMonthEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', organization.id)
        .gte('start_date', startOfLastMonth.toISOString())
        .lte('start_date', endOfLastMonth.toISOString())

      let lastMonthAvg = 0
      if (lastMonthEvents && lastMonthEvents.length > 0) {
        let totalRate = 0
        for (const event of lastMonthEvents) {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)
          
          const eventRate = membersCount > 0 ? ((attendedCount || 0) / membersCount) * 100 : 0
          totalRate += eventRate
        }
        lastMonthAvg = Math.round(totalRate / lastMonthEvents.length)
      }
      setLastMonthRate(lastMonthAvg)

      // Calculate change
      const change = lastMonthAvg > 0 ? ((thisMonthAvg - lastMonthAvg) / lastMonthAvg) * 100 : 0
      setMonthChange(Math.round(change))

    } catch (error) {
      console.error('Load overview data error:', error)
    }
  }

  const loadEventsData = async () => {
    if (!organization) return

    try {
      // Get all past events
      const { data: events } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', organization.id)
        .lte('start_date', new Date().toISOString())
        .order('start_date', { ascending: false })

      if (!events || events.length === 0) {
        setAllEvents([])
        return
      }

      // Get total members for calculation
      const { count: totalMembers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)

      // Calculate attendance for each event
      const eventsWithRates = await Promise.all(
        events.map(async (event) => {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)

          const rate = totalMembers > 0 ? Math.round(((attendedCount || 0) / totalMembers) * 100) : 0

          return {
            ...event,
            attendanceRate: rate,
            attendedCount: attendedCount || 0,
            totalMembers: totalMembers || 0
          }
        })
      )

      setAllEvents(eventsWithRates)

    } catch (error) {
      console.error('Load events data error:', error)
    }
  }

  const loadAttendanceData = async () => {
    if (!organization || !selectedTrendMonth) return

    try {
      const { count: totalMembers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)

      // Get all past events for overview metrics
      const { data: allPastEvents } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', organization.id)
        .lte('start_date', new Date().toISOString())

      if (!allPastEvents || allPastEvents.length === 0) {
        setAvgAttendancePerEvent(0)
        setEventFillRate(0)
        setConfirmationRatio(0)
        setNoShowRate(0)
        setAttendanceTrendData([])
        return
      }

      // Calculate overview metrics from ALL past events
      let totalAttendance = 0
      let totalFillRate = 0
      let totalConfirmed = 0
      let totalNoShows = 0

      for (const event of allPastEvents) {
        const { count: attendedCount } = await supabase
          .from('attendance_records')
          .select('*', { count: 'exact', head: true })
          .eq('event_id', event.id)

        const { count: confirmedCount } = await supabase
          .from('event_confirmations')
          .select('*', { count: 'exact', head: true })
          .eq('event_id', event.id)
          .eq('status', 'confirmed')

        totalAttendance += (attendedCount || 0)
        const fillRate = totalMembers > 0 ? ((attendedCount || 0) / totalMembers) * 100 : 0
        totalFillRate += fillRate
        totalConfirmed += (confirmedCount || 0)

        const noShows = Math.max(0, (confirmedCount || 0) - (attendedCount || 0))
        totalNoShows += noShows
      }

      // Set overview metrics
      setAvgAttendancePerEvent(Math.round(totalAttendance / allPastEvents.length))
      setEventFillRate(Math.round(totalFillRate / allPastEvents.length))
      const ratio = totalConfirmed > 0 ? (totalAttendance / totalConfirmed) * 100 : 0
      setConfirmationRatio(Math.round(ratio))
      const noShowPct = totalConfirmed > 0 ? (totalNoShows / totalConfirmed) * 100 : 0
      setNoShowRate(Math.round(noShowPct))

      // Get events for selected month (for trend chart)
      const [year, month] = selectedTrendMonth.split('-').map(Number)
      const startOfMonth = new Date(year, month - 1, 1)
      const endOfMonth = new Date(year, month, 0, 23, 59, 59)

      const { data: monthEvents } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', organization.id)
        .gte('start_date', startOfMonth.toISOString())
        .lte('start_date', endOfMonth.toISOString())
        .lte('start_date', new Date().toISOString()) // Only past events
        .order('start_date', { ascending: true })

      if (!monthEvents || monthEvents.length === 0) {
        setAttendanceTrendData([])
        return
      }

      // Build trend data for the selected month
      const trendData = await Promise.all(
        monthEvents.map(async (event) => {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)

          const fillRate = totalMembers > 0 ? Math.round(((attendedCount || 0) / totalMembers) * 100) : 0

          return {
            date: format(new Date(event.start_date), 'MMM dd'),
            count: attendedCount || 0,
            rate: fillRate
          }
        })
      )

      setAttendanceTrendData(trendData)

    } catch (error) {
      console.error('Load attendance data error:', error)
    }
  }

  const loadHierarchyData = async () => {
    if (!organization) return

    try {
      const depth = hierarchyLevel === 'team' ? 1 : hierarchyLevel === 'group' ? 2 : 3
      const leadershipLevelName = hierarchyLevel === 'team' ? 'team' : hierarchyLevel === 'group' ? 'group' : 'subgroup'

      // Get groups at the selected depth
      const { data: groupsAtDepth } = await supabase
        .from('groups')
        .select('id, name')
        .eq('org_id', organization.id)
        .eq('depth', depth)

      if (!groupsAtDepth || groupsAtDepth.length === 0) {
        setHierarchyRankings([])
        return
      }

      const { data: pastEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', organization.id)
        .lte('start_date', new Date().toISOString())

      if (!pastEvents || pastEvents.length === 0) {
        setHierarchyRankings([])
        return
      }

      const rankings = await Promise.all(
        groupsAtDepth.map(async (group) => {
          // Get direct members of this group
          const { data: directMembers } = await supabase
            .from('group_members')
            .select('user_id, users!inner(name, leadership_level)')
            .eq('group_id', group.id)

          let allMemberIds = directMembers?.map(m => m.user_id) || []

          // For Team and Group, we need to include members from child groups
          if (depth === 1 || depth === 2) {
            // Get all groups in the organization with all columns to see structure
            const { data: allOrgGroups, error: groupsError } = await supabase
              .from('groups')
              .select('*')
              .eq('org_id', organization.id)

            if (groupsError) {
              console.error('Error fetching groups:', groupsError)
            }

            // Check what parent column exists
            const sampleGroup = allOrgGroups?.[0]

            if (depth === 1 && allOrgGroups) {
              // TEAM: Include Groups (depth 2) and Subgroups (depth 3) under this team
              // Try both parent_id and parent_group_id
              const childGroups = allOrgGroups.filter(g => 
                (g.parent_id === group.id || g.parent_group_id === group.id) && g.depth === 2
              )
              
              for (const childGroup of childGroups) {
                // Get members of this Group
                const { data: groupMembers } = await supabase
                  .from('group_members')
                  .select('user_id')
                  .eq('group_id', childGroup.id)
                allMemberIds.push(...(groupMembers?.map(m => m.user_id) || []))

                // Get Subgroups under this Group
                const grandchildGroups = allOrgGroups.filter(g => 
                  (g.parent_id === childGroup.id || g.parent_group_id === childGroup.id) && g.depth === 3
                )
                for (const grandchildGroup of grandchildGroups) {
                  const { data: subgroupMembers } = await supabase
                    .from('group_members')
                    .select('user_id')
                    .eq('group_id', grandchildGroup.id)
                  allMemberIds.push(...(subgroupMembers?.map(m => m.user_id) || []))
                }
              }

            } else if (depth === 2 && allOrgGroups) {
              // GROUP: Include Subgroups (depth 3) under this group
              const childSubgroups = allOrgGroups.filter(g => 
                (g.parent_id === group.id || g.parent_group_id === group.id) && g.depth === 3
              )
              
              for (const childSubgroup of childSubgroups) {
                const { data: subgroupMembers } = await supabase
                  .from('group_members')
                  .select('user_id')
                  .eq('group_id', childSubgroup.id)
                allMemberIds.push(...(subgroupMembers?.map(m => m.user_id) || []))
              }
            }
          }

          // Remove duplicates
          const uniqueMemberIds = [...new Set(allMemberIds)]

          // Find the leader
          const leader = directMembers?.find(m => m.users?.leadership_level === leadershipLevelName)

          // Calculate attendance using Formula 2
          let totalAttendance = 0
          if (uniqueMemberIds.length > 0) {
            for (const event of pastEvents) {
              const { count: attendedCount } = await supabase
                .from('attendance_records')
                .select('*', { count: 'exact', head: true })
                .eq('event_id', event.id)
                .in('user_id', uniqueMemberIds)

              totalAttendance += (attendedCount || 0)
            }
          }

          const totalPossible = uniqueMemberIds.length * pastEvents.length
          const rate = totalPossible > 0 ? Math.round((totalAttendance / totalPossible) * 100) : 0

          return {
            name: group.name,
            leaderName: leader?.users?.name || 'No leader',
            memberCount: uniqueMemberIds.length,
            rate
          }
        })
      )

      rankings.sort((a, b) => b.rate - a.rate)
      setHierarchyRankings(rankings)

    } catch (error) {
      console.error('Load hierarchy data error:', error)
      console.error('Error details:', error.message)
    }
  }


  const Tooltip = ({ text, children }) => (
    <div className="relative inline-block group">
      {children}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
        <div className="bg-gray-900 text-white text-xs rounded-md py-2 px-3 max-w-[200px] whitespace-normal">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
            <div className="border-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      </div>
    </div>
  )

  // Fetch all export data directly (don't rely on state)
  const fetchExportData = async () => {
    toast.loading('Preparing report data...')
    
    try {
      
      // Fetch Events
      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', organization.id)
        .lte('start_date', new Date().toISOString())
        .order('start_date', { ascending: false })


      const { count: totalMembers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', organization.id)


      const eventsWithRates = await Promise.all(
        (events || []).map(async (event) => {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)

          const rate = totalMembers > 0 ? Math.round(((attendedCount || 0) / totalMembers) * 100) : 0

          return {
            ...event,
            attendanceRate: rate,
            attendedCount: attendedCount || 0,
            totalMembers: totalMembers || 0
          }
        })
      )


      // Fetch Hierarchy
      const depth = hierarchyLevel === 'team' ? 1 : hierarchyLevel === 'group' ? 2 : 3
      const leadershipLevelName = hierarchyLevel === 'team' ? 'team' : hierarchyLevel === 'group' ? 'group' : 'subgroup'


      const { data: groupsAtDepth, error: groupsError } = await supabase
        .from('groups')
        .select('id, name')
        .eq('org_id', organization.id)
        .eq('depth', depth)


      const { data: pastEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', organization.id)
        .lte('start_date', new Date().toISOString())


      let rankings = []
      if (groupsAtDepth && pastEvents && groupsAtDepth.length > 0 && pastEvents.length > 0) {
        const { data: allOrgGroups } = await supabase
          .from('groups')
          .select('*')
          .eq('org_id', organization.id)


        rankings = await Promise.all(
          groupsAtDepth.map(async (group) => {
            const { data: directMembers } = await supabase
              .from('group_members')
              .select('user_id, users!inner(name, leadership_level)')
              .eq('group_id', group.id)

            let allMemberIds = directMembers?.map(m => m.user_id) || []

            if (depth === 1 || depth === 2) {
              if (allOrgGroups) {
                if (depth === 1) {
                  const childGroups = allOrgGroups.filter(g => 
                    (g.parent_id === group.id || g.parent_group_id === group.id) && g.depth === 2
                  )
                  
                  for (const childGroup of childGroups) {
                    const { data: groupMembers } = await supabase
                      .from('group_members')
                      .select('user_id')
                      .eq('group_id', childGroup.id)
                    allMemberIds.push(...(groupMembers?.map(m => m.user_id) || []))

                    const grandchildGroups = allOrgGroups.filter(g => 
                      (g.parent_id === childGroup.id || g.parent_group_id === childGroup.id) && g.depth === 3
                    )
                    for (const grandchildGroup of grandchildGroups) {
                      const { data: subgroupMembers } = await supabase
                        .from('group_members')
                        .select('user_id')
                        .eq('group_id', grandchildGroup.id)
                      allMemberIds.push(...(subgroupMembers?.map(m => m.user_id) || []))
                    }
                  }
                } else if (depth === 2) {
                  const childSubgroups = allOrgGroups.filter(g => 
                    (g.parent_id === group.id || g.parent_group_id === group.id) && g.depth === 3
                  )
                  
                  for (const childSubgroup of childSubgroups) {
                    const { data: subgroupMembers } = await supabase
                      .from('group_members')
                      .select('user_id')
                      .eq('group_id', childSubgroup.id)
                    allMemberIds.push(...(subgroupMembers?.map(m => m.user_id) || []))
                  }
                }
              }
            }

            const uniqueMemberIds = [...new Set(allMemberIds)]
            const leader = directMembers?.find(m => m.users?.leadership_level === leadershipLevelName)

            let totalAttendance = 0
            if (uniqueMemberIds.length > 0) {
              for (const event of pastEvents) {
                const { count: attendedCount } = await supabase
                  .from('attendance_records')
                  .select('*', { count: 'exact', head: true })
                  .eq('event_id', event.id)
                  .in('user_id', uniqueMemberIds)

                totalAttendance += (attendedCount || 0)
              }
            }

            const totalPossible = uniqueMemberIds.length * pastEvents.length
            const rate = totalPossible > 0 ? Math.round((totalAttendance / totalPossible) * 100) : 0

            return {
              name: group.name,
              leaderName: leader?.users?.name || 'No leader',
              memberCount: uniqueMemberIds.length,
              rate
            }
          })
        )

        rankings.sort((a, b) => b.rate - a.rate)
      }

      
      // Calculate Attendance Metrics
      
      let calcAvgAttendancePerEvent = 0
      let calcEventFillRate = 0
      let calcConfirmationRatio = 0
      let calcNoShowRate = 0
      
      if (pastEvents && pastEvents.length > 0) {
        // Total attendance across all events
        let totalAttendanceCount = 0
        let totalConfirmed = 0
        let totalNoShows = 0
        let eventRates = []
        
        for (const event of pastEvents) {
          const { count: attendedCount } = await supabase
            .from('attendance_records')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)
          
          const { count: confirmedCount } = await supabase
            .from('event_confirmations')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)
            .eq('status', 'confirmed')
          
          const { count: declinedCount } = await supabase
            .from('event_confirmations')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id)
            .eq('status', 'declined')
          
          totalAttendanceCount += (attendedCount || 0)
          totalConfirmed += (confirmedCount || 0)
          
          // No-shows = confirmed but didn't attend
          const noShows = Math.max(0, (confirmedCount || 0) - (attendedCount || 0))
          totalNoShows += noShows
          
          // Event fill rate (Formula 3 from guide)
          const eventRate = totalMembers > 0 ? ((attendedCount || 0) / totalMembers) * 100 : 0
          eventRates.push(eventRate)
        }
        
        // Avg Per Event = Total attendance ÷ # events
        calcAvgAttendancePerEvent = Math.round(totalAttendanceCount / pastEvents.length)
        
        // Event Fill Rate = Average of all event rates (Formula 3)
        calcEventFillRate = eventRates.length > 0 
          ? Math.round(eventRates.reduce((sum, rate) => sum + rate, 0) / eventRates.length)
          : 0
        
        // Confirmation Ratio = (Attended ÷ Confirmed) × 100
        calcConfirmationRatio = totalConfirmed > 0 
          ? Math.round((totalAttendanceCount / totalConfirmed) * 100)
          : 0
        
        // No-Show Rate = (No-shows ÷ Confirmed) × 100
        calcNoShowRate = totalConfirmed > 0 
          ? Math.round((totalNoShows / totalConfirmed) * 100)
          : 0
      }
      
      toast.dismiss()
      return {
        events: eventsWithRates,
        hierarchy: rankings,
        attendance: {
          avgPerEvent: calcAvgAttendancePerEvent,
          fillRate: calcEventFillRate,
          confirmationRatio: calcConfirmationRatio,
          noShowRate: calcNoShowRate
        }
      }
    } catch (error) {
      console.error('Error fetching export data:', error)
      toast.dismiss()
      toast.error('Failed to prepare export data')
      return {
        events: [],
        hierarchy: [],
        attendance: {
          avgPerEvent: 0,
          fillRate: 0,
          confirmationRatio: 0,
          noShowRate: 0
        }
      }
    }
  }

  // Export Functions
  const exportToPDF = async () => {
    try {
      // Fetch fresh data directly
      const exportData = await fetchExportData()
      if (!exportData) {
        toast.error('Failed to fetch report data')
        return
      }

      const { events: eventsData, hierarchy: hierarchyData, attendance: attendanceData } = exportData
      
      const doc = new jsPDF()
    
    // Cover Page
    doc.setFillColor(37, 99, 235) // Blue
    doc.rect(0, 0, 210, 297, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(32)
    doc.text('Attendance Report', 105, 120, { align: 'center' })
    doc.setFontSize(16)
    doc.text(organization?.name || 'Organization', 105, 140, { align: 'center' })
    doc.setFontSize(12)
    doc.text(`Generated: ${format(new Date(), 'MMMM dd, yyyy')}`, 105, 160, { align: 'center' })
    
    // Page 2: Overview
    doc.addPage()
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(20)
    doc.text('Overview Report', 14, 20)
    doc.setFontSize(10)
    doc.text(`Period: ${monthOptions.find(m => m.value === selectedMonth1)?.label || 'Current Month'}`, 14, 28)
    
    const overviewData = [
      ['Total Members', month1Data.totalMembers.toString()],
      ['Avg Attendance Rate', `${month1Data.avgAttendanceRate}%`],
      ['Total Events', month1Data.totalEvents.toString()],
      ['Unassigned Members', month1Data.unassignedMembers.toString()],
      ['Teams', month1Data.totalTeams.toString()],
      ['Groups', month1Data.totalGroups.toString()],
      ['Subgroups', month1Data.totalSubgroups.toString()]
    ]
    
    autoTable(doc, {
      startY: 35,
      head: [['Metric', 'Value']],
      body: overviewData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 14, right: 14 }
    })

    // Page 3: Events
    doc.addPage()
    doc.setFontSize(20)
    doc.text('Event Performance', 14, 20)
    doc.setFontSize(10)
    doc.text(`Total Events: ${eventsData.length}`, 14, 28)
    
    const eventsDataForPDF = eventsData.map(event => [
      event.title.length > 30 ? event.title.substring(0, 30) + '...' : event.title,
      format(new Date(event.start_date), 'MMM dd, yyyy'),
      (event.event_type || 'Physical').charAt(0).toUpperCase() + (event.event_type || 'physical').slice(1),
      `${event.attendedCount}/${event.totalMembers}`,
      `${event.attendanceRate}%`
    ])
    
    autoTable(doc, {
      startY: 35,
      head: [['Event Name', 'Date', 'Type', 'Attended', 'Rate']],
      body: eventsDataForPDF,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8 }
    })

    // Page 4: Attendance Overview
    const finalY = doc.lastAutoTable?.finalY || 35
    if (finalY > 200) {
      doc.addPage()
    }
    
    doc.setFontSize(20)
    doc.text('Attendance Overview', 14, finalY > 200 ? 20 : finalY + 15)
    
    const attendanceTableData = [
      ['Avg Per Event', attendanceData.avgPerEvent.toString()],
      ['Event Fill Rate', `${attendanceData.fillRate}%`],
      ['Confirmation Ratio', `${attendanceData.confirmationRatio}%`],
      ['No-Show Rate', `${attendanceData.noShowRate}%`]
    ]
    
    autoTable(doc, {
      startY: finalY > 200 ? 28 : finalY + 23,
      head: [['Metric', 'Value']],
      body: attendanceTableData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 14, right: 14 }
    })

    // Page 5: Hierarchy Rankings
    doc.addPage()
    doc.setFontSize(20)
    doc.text(`Hierarchy Performance: ${hierarchyLevel.charAt(0).toUpperCase() + hierarchyLevel.slice(1)}s`, 14, 20)
    doc.setFontSize(10)
    doc.text(`Total ${hierarchyLevel}s: ${hierarchyData.length}`, 14, 28)
    
    const hierarchyDataForPDF = hierarchyData.map((item, index) => [
      (index + 1).toString(),
      item.name.length > 25 ? item.name.substring(0, 25) + '...' : item.name,
      item.leaderName.length > 20 ? item.leaderName.substring(0, 20) + '...' : item.leaderName,
      item.memberCount.toString(),
      `${item.rate}%`
    ])
    
    autoTable(doc, {
      startY: 35,
      head: [['#', 'Name', 'Leader', 'Members', 'Rate']],
      body: hierarchyDataForPDF,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 }
    })

    doc.save(`attendance-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`)
    toast.success('PDF report exported successfully!')
    setShowExportDropdown(false)
    } catch (error) {
      console.error('PDF Export Error:', error)
      toast.error('Failed to generate PDF report. Please try again.')
      setShowExportDropdown(false)
    }
  }

  const exportToPPTX = async () => {
    try {
      // Fetch fresh data directly
      const exportData = await fetchExportData()
      if (!exportData) {
        toast.error('Failed to fetch report data')
        return
      }

      const { events: eventsData, hierarchy: hierarchyData, attendance: attendanceData } = exportData
    
    
    const pptx = new pptxgen()
    
    // Set presentation properties
    pptx.author = organization?.name || 'Organization'
    pptx.company = organization?.name || 'Organization'
    pptx.title = 'Attendance Report'
    pptx.subject = 'Attendance Analytics and Performance Metrics'
    
    // Slide 1: Title/Cover
    const slide1 = pptx.addSlide({ masterName: 'TITLE_SLIDE' })
    slide1.background = { color: '2563EB' }
    slide1.addText('Attendance Report', {
      x: 0.5,
      y: 1.5,
      w: 9,
      h: 1,
      fontSize: 44,
      bold: true,
      color: 'FFFFFF',
      align: 'center',
      isTextBox: true
    })
    slide1.addText(`${organization?.name || 'Organization'}`, {
      x: 0.5,
      y: 2.5,
      w: 9,
      h: 0.5,
      fontSize: 24,
      color: 'FFFFFF',
      align: 'center',
      isTextBox: true
    })
    slide1.addText(`Generated: ${format(new Date(), 'MMMM dd, yyyy')}`, {
      x: 0.5,
      y: 3.2,
      w: 9,
      h: 0.5,
      fontSize: 18,
      color: 'FFFFFF',
      align: 'center',
      isTextBox: true
    })

    // Slide 2: Overview
    const slide2 = pptx.addSlide()
    slide2.addText([
      { text: 'Overview Report', options: { fontSize: 28, bold: true, color: '1F2937', breakLine: true } }
    ], {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.6,
      isTextBox: true
    })
    slide2.addText(`Period: ${monthOptions.find(m => m.value === selectedMonth1)?.label || 'Current Month'}`, {
      x: 0.5,
      y: 0.8,
      w: 9,
      h: 0.3,
      fontSize: 14,
      color: '6B7280',
      isTextBox: true
    })
    
    slide2.addTable([
      [
        { text: 'Metric', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Value', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } }
      ],
      ['Total Members', month1Data.totalMembers.toString()],
      ['Avg Attendance Rate', `${month1Data.avgAttendanceRate}%`],
      ['Total Events', month1Data.totalEvents.toString()],
      ['Unassigned Members', month1Data.unassignedMembers.toString()],
      ['Teams', month1Data.totalTeams.toString()],
      ['Groups', month1Data.totalGroups.toString()],
      ['Subgroups', month1Data.totalSubgroups.toString()]
    ], {
      x: 0.5,
      y: 1.3,
      w: 9,
      colW: [4.5, 4.5],
      border: { pt: 1, color: 'E5E7EB' },
      fill: { color: 'F9FAFB' },
      color: '374151',
      fontSize: 14,
      valign: 'middle',
      rowH: 0.4
    })

    // Slide 3: Events
    const slide3 = pptx.addSlide()
    slide3.addText([
      { text: 'Event Performance', options: { fontSize: 28, bold: true, color: '1F2937', breakLine: true } }
    ], {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.6,
      isTextBox: true
    })
    slide3.addText(`Total Events: ${eventsData.length}`, {
      x: 0.5,
      y: 0.8,
      w: 9,
      h: 0.3,
      fontSize: 14,
      color: '6B7280',
      isTextBox: true
    })
    
    const eventRows = [
      [
        { text: 'Event Name', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Date', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Type', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Attended', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Rate', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } }
      ]
    ]
    
    eventsData.slice(0, 15).forEach(event => {
      eventRows.push([
        event.title.length > 30 ? event.title.substring(0, 30) + '...' : event.title,
        format(new Date(event.start_date), 'MMM dd, yyyy'),
        (event.event_type || 'Physical').charAt(0).toUpperCase() + (event.event_type || 'physical').slice(1),
        `${event.attendedCount}/${event.totalMembers}`,
        `${event.attendanceRate}%`
      ])
    })
    
    slide3.addTable(eventRows, {
      x: 0.5,
      y: 1.3,
      w: 9,
      colW: [3, 1.5, 1.2, 1.3, 1],
      border: { pt: 1, color: 'E5E7EB' },
      fill: { color: 'F9FAFB' },
      color: '374151',
      fontSize: 10,
      valign: 'middle',
      rowH: 0.35
    })

    // Slide 4: Attendance Overview
    const slide4 = pptx.addSlide()
    slide4.addText([
      { text: 'Attendance Overview', options: { fontSize: 28, bold: true, color: '1F2937', breakLine: true } }
    ], {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.6,
      isTextBox: true
    })
    
    slide4.addTable([
      [
        { text: 'Metric', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Value', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } }
      ],
      ['Avg Per Event', attendanceData.avgPerEvent.toString()],
      ['Event Fill Rate', `${attendanceData.fillRate}%`],
      ['Confirmation Ratio', `${attendanceData.confirmationRatio}%`],
      ['No-Show Rate', `${attendanceData.noShowRate}%`]
    ], {
      x: 0.5,
      y: 1.3,
      w: 9,
      colW: [4.5, 4.5],
      border: { pt: 1, color: 'E5E7EB' },
      fill: { color: 'F9FAFB' },
      color: '374151',
      fontSize: 14,
      valign: 'middle',
      rowH: 0.5
    })

    // Slide 5: Hierarchy Rankings
    const slide5 = pptx.addSlide()
    slide5.addText([
      { text: `Hierarchy: ${hierarchyLevel.charAt(0).toUpperCase() + hierarchyLevel.slice(1)}s`, options: { fontSize: 28, bold: true, color: '1F2937', breakLine: true } }
    ], {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.6,
      isTextBox: true
    })
    slide5.addText(`Total ${hierarchyLevel}s: ${hierarchyData.length}`, {
      x: 0.5,
      y: 0.8,
      w: 9,
      h: 0.3,
      fontSize: 14,
      color: '6B7280',
      isTextBox: true
    })
    
    const hierarchyRows = [
      [
        { text: '#', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Name', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Leader', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Members', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } },
        { text: 'Rate', options: { bold: true, color: 'FFFFFF', fill: '2563EB' } }
      ]
    ]
    
    hierarchyData.slice(0, 15).forEach((item, index) => {
      hierarchyRows.push([
        (index + 1).toString(),
        item.name.length > 25 ? item.name.substring(0, 25) + '...' : item.name,
        item.leaderName.length > 20 ? item.leaderName.substring(0, 20) + '...' : item.leaderName,
        item.memberCount.toString(),
        `${item.rate}%`
      ])
    })
    
    slide5.addTable(hierarchyRows, {
      x: 0.5,
      y: 1.3,
      w: 9,
      colW: [0.5, 3.5, 2.5, 1.5, 1],
      border: { pt: 1, color: 'E5E7EB' },
      fill: { color: 'F9FAFB' },
      color: '374151',
      fontSize: 11,
      valign: 'middle',
      rowH: 0.35
    })

    pptx.writeFile({ fileName: `attendance-report-${format(new Date(), 'yyyy-MM-dd')}.pptx` })
    toast.success('PowerPoint report exported successfully!')
    setShowExportDropdown(false)
    } catch (error) {
      console.error('PowerPoint Export Error:', error)
      toast.error('Failed to generate PowerPoint report. Please try again.')
      setShowExportDropdown(false)
    }
  }

  const exportToCSV = async () => {
    try {
      // Fetch fresh data directly
      const exportData = await fetchExportData()
      if (!exportData) {
        toast.error('Failed to fetch report data')
        return
      }

      const { events: eventsData, hierarchy: hierarchyData, attendance: attendanceData } = exportData
      
      // Create a new workbook
      const workbook = new ExcelJS.Workbook()
    workbook.creator = organization?.name || 'Organization'
    workbook.created = new Date()

    // Sheet 1: Overview
    const overviewSheet = workbook.addWorksheet('Overview')
    overviewSheet.addRow(['Attendance Report'])
    overviewSheet.addRow([`Organization: ${organization?.name || 'N/A'}`])
    overviewSheet.addRow([`Generated: ${format(new Date(), 'MMMM dd, yyyy')}`])
    overviewSheet.addRow([])
    overviewSheet.addRow(['OVERVIEW REPORT'])
    overviewSheet.addRow([`Period: ${monthOptions.find(m => m.value === selectedMonth1)?.label || 'Current Month'}`])
    overviewSheet.addRow([])
    
    // Header row with styling
    const overviewHeaderRow = overviewSheet.addRow(['Metric', 'Value'])
    overviewHeaderRow.font = { bold: true }
    overviewHeaderRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF2563EB' }
    }
    overviewHeaderRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    
    overviewSheet.addRow(['Total Members', month1Data.totalMembers])
    overviewSheet.addRow(['Avg Attendance Rate', `${month1Data.avgAttendanceRate}%`])
    overviewSheet.addRow(['Total Events', month1Data.totalEvents])
    overviewSheet.addRow(['Unassigned Members', month1Data.unassignedMembers])
    overviewSheet.addRow(['Teams', month1Data.totalTeams])
    overviewSheet.addRow(['Groups', month1Data.totalGroups])
    overviewSheet.addRow(['Subgroups', month1Data.totalSubgroups])
    
    overviewSheet.columns = [
      { width: 25 },
      { width: 15 }
    ]

    // Sheet 2: Events
    const eventsSheet = workbook.addWorksheet('Events')
    eventsSheet.addRow(['EVENT PERFORMANCE'])
    eventsSheet.addRow([`Total Events: ${eventsData.length}`])
    eventsSheet.addRow([])
    
    const eventsHeaderRow = eventsSheet.addRow(['Event Name', 'Date', 'Type', 'Attended', 'Total Members', 'Attendance Rate'])
    eventsHeaderRow.font = { bold: true }
    eventsHeaderRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF2563EB' }
    }
    eventsHeaderRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    
    eventsData.forEach(event => {
      eventsSheet.addRow([
        event.title,
        format(new Date(event.start_date), 'MMM dd, yyyy'),
        (event.event_type || 'Physical').charAt(0).toUpperCase() + (event.event_type || 'physical').slice(1),
        event.attendedCount,
        event.totalMembers,
        `${event.attendanceRate}%`
      ])
    })
    
    eventsSheet.columns = [
      { width: 40 },
      { width: 15 },
      { width: 12 },
      { width: 12 },
      { width: 15 },
      { width: 15 }
    ]

    // Sheet 3: Attendance
    const attendanceSheet = workbook.addWorksheet('Attendance')
    attendanceSheet.addRow(['ATTENDANCE OVERVIEW'])
    attendanceSheet.addRow([])
    
    const attendanceHeaderRow = attendanceSheet.addRow(['Metric', 'Value'])
    attendanceHeaderRow.font = { bold: true }
    attendanceHeaderRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF2563EB' }
    }
    attendanceHeaderRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    
    attendanceSheet.addRow(['Avg Per Event', attendanceData.avgPerEvent])
    attendanceSheet.addRow(['Event Fill Rate', `${attendanceData.fillRate}%`])
    attendanceSheet.addRow(['Confirmation Ratio', `${attendanceData.confirmationRatio}%`])
    attendanceSheet.addRow(['No-Show Rate', `${attendanceData.noShowRate}%`])
    
    attendanceSheet.columns = [
      { width: 25 },
      { width: 15 }
    ]

    // Sheet 4: Hierarchy
    const hierarchySheet = workbook.addWorksheet('Hierarchy')
    hierarchySheet.addRow(['HIERARCHY PERFORMANCE'])
    hierarchySheet.addRow([`Level: ${hierarchyLevel.charAt(0).toUpperCase() + hierarchyLevel.slice(1)}s`])
    hierarchySheet.addRow([`Total: ${hierarchyData.length}`])
    hierarchySheet.addRow([])
    
    const hierarchyHeaderRow = hierarchySheet.addRow(['Rank', 'Name', 'Leader', 'Members', 'Attendance Rate'])
    hierarchyHeaderRow.font = { bold: true }
    hierarchyHeaderRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF2563EB' }
    }
    hierarchyHeaderRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    
    hierarchyData.forEach((item, index) => {
      hierarchySheet.addRow([
        index + 1,
        item.name,
        item.leaderName,
        item.memberCount,
        `${item.rate}%`
      ])
    })
    
    hierarchySheet.columns = [
      { width: 8 },
      { width: 30 },
      { width: 25 },
      { width: 12 },
      { width: 15 }
    ]

    // Generate buffer and download
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `attendance-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`
    link.click()
    
    toast.success('Excel report exported successfully!')
    setShowExportDropdown(false)
    } catch (error) {
      console.error('Excel Export Error:', error)
      toast.error('Failed to generate Excel report. Please try again.')
      setShowExportDropdown(false)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading reports..." />
  }

  return (
    <DashboardLayout>
      <PageHeader
        title="Reports & Analytics"
        description="View attendance trends, performance metrics, and insights"
      />

      <div className="p-6">
        {/* Single White Card Container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Tab Navigation */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-[#EAF0FF] text-[#2228B6] border border-[#2228B6] shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon size={18} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                    {tab.label}
                  </button>
                )
              })}
            </div>

            {/* Export Button */}
            <div className="relative export-dropdown-container">
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#2228B6] text-white rounded-lg text-sm font-medium hover:bg-[#2228B6] transition-all"
              >
                <DownloadSimple size={18} weight="bold" />
                Export Report
                <CaretDown size={16} weight="bold" />
              </button>

              {/* Dropdown Menu */}
              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <button
                    onClick={exportToPDF}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FilePdf weight="fill" size={20} />
                    As PDF
                  </button>
                  <button
                    onClick={exportToPPTX}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FilePpt weight="fill" size={20} />
                    As PPTX
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileXls weight="fill" size={20} />
                    As Excel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="space-y-6">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview Reports</h2>

              {/* Period Comparison Filter */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Compare:</span>
                  <select
                    value={selectedMonth1}
                    onChange={(e) => setSelectedMonth1(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {monthOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm font-medium text-gray-700">vs</span>
                  <select
                    value={selectedMonth2}
                    onChange={(e) => setSelectedMonth2(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {monthOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Organization Metrics */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Organization Metrics</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Total Members */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Total Members</p>
                      <Tooltip text="Total number of active members in your organization">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900 mb-3">{month1Data.totalMembers}</p>
                    {(() => {
                      const comparison = getComparison(month1Data.totalMembers, month2Data.totalMembers)
                      return (
                        <div className={`flex items-center gap-1 text-sm font-medium ${comparison.color}`}>
                          {comparison.icon === 'up' && <ArrowUpRight size={16} weight="bold" />}
                          {comparison.icon === 'down' && <ArrowDownLeft size={16} weight="bold" />}
                          {comparison.icon === 'same' && <Minus size={16} weight="bold" />}
                          <span>{comparison.text}</span>
                        </div>
                      )
                    })()}
                  </div>

                  {/* Avg Attendance Rate */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Avg Attendance</p>
                      <Tooltip text="Average attendance rate across all events in the period">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900 mb-3">{month1Data.avgAttendanceRate}%</p>
                    {(() => {
                      const comparison = getComparison(month1Data.avgAttendanceRate, month2Data.avgAttendanceRate, true)
                      return (
                        <div className={`flex items-center gap-1 text-sm font-medium ${comparison.color}`}>
                          {comparison.icon === 'up' && <ArrowUpRight size={16} weight="bold" />}
                          {comparison.icon === 'down' && <ArrowDownLeft size={16} weight="bold" />}
                          {comparison.icon === 'same' && <Minus size={16} weight="bold" />}
                          <span>{comparison.text}</span>
                        </div>
                      )
                    })()}
                  </div>

                  {/* Total Events */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Total Events</p>
                      <Tooltip text="Total number of events created in the period">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900 mb-3">{month1Data.totalEvents}</p>
                    {(() => {
                      const comparison = getComparison(month1Data.totalEvents, month2Data.totalEvents)
                      return (
                        <div className={`flex items-center gap-1 text-sm font-medium ${comparison.color}`}>
                          {comparison.icon === 'up' && <ArrowUpRight size={16} weight="bold" />}
                          {comparison.icon === 'down' && <ArrowDownLeft size={16} weight="bold" />}
                          {comparison.icon === 'same' && <Minus size={16} weight="bold" />}
                          <span>{comparison.text}</span>
                        </div>
                      )
                    })()}
                  </div>

                  {/* Unassigned Members */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Unassigned</p>
                      <Tooltip text="Members not assigned to any team or group">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900 mb-3">{month1Data.unassignedMembers}</p>
                    {(() => {
                      const comparison = getComparison(month1Data.unassignedMembers, month2Data.unassignedMembers)
                      return (
                        <div className={`flex items-center gap-1 text-sm font-medium ${comparison.color}`}>
                          {comparison.icon === 'up' && <ArrowUpRight size={16} weight="bold" />}
                          {comparison.icon === 'down' && <ArrowDownLeft size={16} weight="bold" />}
                          {comparison.icon === 'same' && <Minus size={16} weight="bold" />}
                          <span>{comparison.text}</span>
                        </div>
                      )
                    })()}
                  </div>

                  {/* Teams */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Teams</p>
                      <Tooltip text="Number of teams in the organization">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900 mb-3">{month1Data.totalTeams}</p>
                    {(() => {
                      const comparison = getComparison(month1Data.totalTeams, month2Data.totalTeams)
                      return (
                        <div className={`flex items-center gap-1 text-sm font-medium ${comparison.color}`}>
                          {comparison.icon === 'up' && <ArrowUpRight size={16} weight="bold" />}
                          {comparison.icon === 'down' && <ArrowDownLeft size={16} weight="bold" />}
                          {comparison.icon === 'same' && <Minus size={16} weight="bold" />}
                          <span>{comparison.text}</span>
                        </div>
                      )
                    })()}
                  </div>

                  {/* Groups */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Groups</p>
                      <Tooltip text="Number of groups in the organization">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900 mb-3">{month1Data.totalGroups}</p>
                    {(() => {
                      const comparison = getComparison(month1Data.totalGroups, month2Data.totalGroups)
                      return (
                        <div className={`flex items-center gap-1 text-sm font-medium ${comparison.color}`}>
                          {comparison.icon === 'up' && <ArrowUpRight size={16} weight="bold" />}
                          {comparison.icon === 'down' && <ArrowDownLeft size={16} weight="bold" />}
                          {comparison.icon === 'same' && <Minus size={16} weight="bold" />}
                          <span>{comparison.text}</span>
                        </div>
                      )
                    })()}
                  </div>

                  {/* Subgroups */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Subgroups</p>
                      <Tooltip text="Number of subgroups in the organization">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900 mb-3">{month1Data.totalSubgroups}</p>
                    {(() => {
                      const comparison = getComparison(month1Data.totalSubgroups, month2Data.totalSubgroups)
                      return (
                        <div className={`flex items-center gap-1 text-sm font-medium ${comparison.color}`}>
                          {comparison.icon === 'up' && <ArrowUpRight size={16} weight="bold" />}
                          {comparison.icon === 'down' && <ArrowDownLeft size={16} weight="bold" />}
                          {comparison.icon === 'same' && <Minus size={16} weight="bold" />}
                          <span>{comparison.text}</span>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* EVENTS TAB */}
          {activeTab === 'events' && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Event Performance Reports</h2>

              {/* Filters */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Event Type:</span>
                    <select
                      value={eventTypeFilter}
                      onChange={(e) => setEventTypeFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Events</option>
                      <option value="physical">Physical</option>
                      <option value="virtual">Virtual</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Sort By:</span>
                    <select
                      value={eventSortOrder}
                      onChange={(e) => setEventSortOrder(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="highest">Highest Rate</option>
                      <option value="lowest">Lowest Rate</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Events Table */}
              <div className="border-t border-gray-200 pt-6">
                {(() => {
                  // Filter events
                  let filteredEvents = allEvents
                  if (eventTypeFilter !== 'all') {
                    filteredEvents = allEvents.filter(event => 
                      (event.event_type || 'physical').toLowerCase() === eventTypeFilter
                    )
                  }

                  // Sort events
                  const sortedEvents = [...filteredEvents].sort((a, b) => {
                    if (eventSortOrder === 'highest') {
                      return b.attendanceRate - a.attendanceRate
                    } else {
                      return a.attendanceRate - b.attendanceRate
                    }
                  })

                  if (sortedEvents.length === 0) {
                    return <p className="text-center py-8 text-gray-500">No events found</p>
                  }

                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Event Name</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Attended</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Attendance Rate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {sortedEvents.map((event) => (
                            <tr key={event.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{event.title}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {format(new Date(event.start_date), 'MMM dd, yyyy')}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  (event.event_type || 'physical') === 'physical' ? 'bg-blue-100 text-blue-700' :
                                  (event.event_type || 'physical') === 'virtual' ? 'bg-purple-100 text-purple-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {(event.event_type || 'Physical').charAt(0).toUpperCase() + (event.event_type || 'physical').slice(1)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <span className="font-semibold">{event.attendedCount}</span>
                                <span className="text-gray-500"> / {event.totalMembers}</span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`font-semibold ${
                                  event.attendanceRate >= 80 ? 'text-green-600' :
                                  event.attendanceRate >= 60 ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {event.attendanceRate}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                })()}
              </div>
            </>
          )}

          {/* ATTENDANCE TAB */}
          {activeTab === 'attendance' && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Attendance Performance Reports</h2>

              {/* Event Attendance Overview */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Event Attendance Overview</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Avg Per Event */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Avg Per Event</p>
                      <Tooltip text="Average number of attendees per event">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900">{avgAttendancePerEvent}</p>
                  </div>

                  {/* Event Fill Rate */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Event Fill Rate</p>
                      <Tooltip text="Average percentage of members attending each event">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900">{eventFillRate}%</p>
                  </div>

                  {/* Confirmation Ratio */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">Confirmation Ratio</p>
                      <Tooltip text="Percentage of confirmed members who actually attended">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900">{confirmationRatio}%</p>
                  </div>

                  {/* No-Show Rate */}
                  <div className="bg-white rounded-xl p-8 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-700">No-Show Rate</p>
                      <Tooltip text="Percentage of confirmed members who didn't attend">
                        <Info size={16} className="text-gray-500" />
                      </Tooltip>
                    </div>
                    <p className="text-6xl font-bold text-gray-900">{noShowRate}%</p>
                  </div>
                </div>
              </div>

              {/* Event Attendance Trends */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">Event Attendance Trends</h3>
                    <Tooltip text="Attendance count and fill rate for events in the selected month">
                      <Info size={18} className="text-gray-500" />
                    </Tooltip>
                  </div>

                  {/* Month/Year Filter */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Month:</span>
                    <select
                      value={selectedTrendMonth}
                      onChange={(e) => setSelectedTrendMonth(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {monthOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {attendanceTrendData.length === 0 ? (
                  <p className="text-center py-12 text-gray-500">No events found for the selected month</p>
                ) : (
                  <div className="w-full">
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={attendanceTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="date" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <YAxis 
                          yAxisId="left"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fill: '#6B7280' } }}
                        />
                        <YAxis 
                          yAxisId="right" 
                          orientation="right"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          label={{ value: 'Fill Rate (%)', angle: 90, position: 'insideRight', style: { fill: '#6B7280' } }}
                        />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #E5E7EB', 
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                        />
                        <RechartsLegend 
                          wrapperStyle={{ paddingTop: '20px' }}
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="count" 
                          stroke="#3b82f6" 
                          strokeWidth={3}
                          name="Attendance Count"
                          dot={{ fill: '#3b82f6', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="rate" 
                          stroke="#10b981" 
                          strokeWidth={3}
                          name="Fill Rate (%)"
                          dot={{ fill: '#10b981', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
          )}

          {/* HIERARCHY TAB */}
          {activeTab === 'hierarchy' && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Hierarchy Performance Reports</h2>

              <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">Performance Rankings</h3>
                    <Tooltip text="Attendance performance by organizational level">
                      <Info size={18} className="text-gray-500" />
                    </Tooltip>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setHierarchyLevel('team')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        hierarchyLevel === 'team'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Teams
                    </button>
                    <button
                      onClick={() => setHierarchyLevel('group')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        hierarchyLevel === 'group'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Sub teams
                    </button>
                    <button
                      onClick={() => setHierarchyLevel('subgroup')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        hierarchyLevel === 'subgroup'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Departments
                    </button>
                  </div>
                </div>

                {hierarchyRankings.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No data available</p>
                ) : (
                  <>
                    {/* Chart */}
                    <div className="w-full mb-6">
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart 
                          data={hierarchyRankings} 
                          layout="vertical"
                          margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            type="number"
                            domain={[0, 100]}
                            tick={{ fill: '#6B7280', fontSize: 12 }}
                            tickFormatter={(value) => `${value}%`}
                          />
                          <YAxis 
                            type="category"
                            dataKey="name"
                            tick={{ fill: '#6B7280', fontSize: 12 }}
                            width={140}
                          />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #E5E7EB', 
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                            }}
                            formatter={(value) => `${value}%`}
                            labelFormatter={(label) => `${label}`}
                          />
                          <Bar dataKey="rate" radius={[0, 8, 8, 0]}>
                            {hierarchyRankings.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.rate > 75 ? '#10b981' : entry.rate > 50 ? '#3b82f6' : '#ef4444'} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Leader</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Members</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Attendance Rate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {hierarchyRankings.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.leaderName}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.memberCount}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`font-semibold ${
                                  item.rate >= 80 ? 'text-green-600' :
                                  item.rate >= 60 ? 'text-blue-600' :
                                  'text-red-600'
                                }`}>
                                  {item.rate}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}